import * as Cesium from "cesium";
import { cloneEntity, getUsernameAndUserIdFormat, showToast } from "./Utility";

import { arcDrawingActive } from "./editing/CreateEntityOnQuadraticBezier";
import { toggleDirectionalArrows } from "./geojson/Arrow";
import { resetEntityStyle } from "./other/MultiSelection";
import { restoreCameraControls } from "./MapUtility";

let activeReverseHandler = null;

function safeGetById(entityCollection, id) {
  if (!entityCollection || id == null) return undefined;
  try {
    return entityCollection.getById(id);
  } catch {
    return undefined;
  }
}

export const enableReverseOnClick = (viewer, callback, entity, isReturnHandler = false, directionArrowSettingRef, userData) => {
  // Helper function to reverse coordinates of an entity
  function reverseEntity(entity) {
    // Reverse polyline
    if (Cesium.defined(entity.polyline) && Cesium.defined(entity.polyline.positions)) {
      const positions = entity.polyline.positions.getValue(Cesium.JulianDate.now());
      const reversed = positions.slice().reverse();
      entity.polyline.positions = new Cesium.ConstantProperty(reversed);
    }

    // Reverse polygon
    if (Cesium.defined(entity.polygon) && Cesium.defined(entity.polygon.hierarchy)) {
      const hierarchy = entity.polygon.hierarchy.getValue(Cesium.JulianDate.now());
      const reversed = hierarchy.positions.slice().reverse();
      entity.polygon.hierarchy = new Cesium.ConstantProperty(new Cesium.PolygonHierarchy(reversed));
    }

    // Set modifier property
    if (!entity.properties) {
      entity.properties = new Cesium.PropertyBag();
    }

    if (typeof entity.properties.addProperty === "function" && !entity.properties.modifier) {
      entity.properties.addProperty("modifier");
    }

    entity.properties.modifier = new Cesium.ConstantProperty(getUsernameAndUserIdFormat(userData));

    // Call user callback if provided
    if (typeof callback === "function") {
      callback(entity);
    }

    /* ------------------- NEW: Update directional arrows ------------------- */
    const ds = entity?.entityCollection?._owner;
    if (ds) {
      const isVisible = !!(ds._arrowBillboards && ds._arrowBillboards.length > 0);
      toggleDirectionalArrows(viewer, ds, isVisible, directionArrowSettingRef);
    }
  }

  // ✅ Case 1: Entity is passed directly
  if (Cesium.defined(entity)) {
    reverseEntity(entity);
    return;
  }

  // ✅ Case 2: User clicks an entity
  if (!isReturnHandler) {
    // one-shot mode: remove any previous handler
    if (activeReverseHandler) {
      activeReverseHandler.destroy();
      activeReverseHandler = null;
    }
  }

  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  handler.setInputAction(function (click) {
    const pickedObject = viewer.scene.pick(click.position);

    if (!Cesium.defined(pickedObject) || !Cesium.defined(pickedObject.id)) {
      return;
    }

    reverseEntity(pickedObject.id);

    // Auto destroy only if not return mode
    if (!isReturnHandler) {
      handler.destroy();
      activeReverseHandler = null;
    }
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  if (!isReturnHandler) {
    activeReverseHandler = handler;
  } else {
    return handler; // user will manage destroy manually
  }
};

export const updateEntityPropertiesFromFlatList = (entity, flatList) => {
  if (!entity) return;

  if (!entity.properties) {
    entity.properties = new Cesium.PropertyBag({});
  }

  const now = Cesium.JulianDate.now();

  flatList.forEach(({ key, value }) => {
    const parts = key.split(".");
    let current = entity.properties;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      const isLast = i === parts.length - 1;

      if (isLast) {
        // Set final value
        if (typeof current.setProperty === "function") {
          current.setProperty(part, value);
        } else {
          current[part] = value;
        }
      } else {
        // Go deeper into nested property
        let next = current[part];

        if (!next || typeof next.getValue !== "function") {
          // Doesn't exist or not reactive -> create PropertyBag
          const newBag = new Cesium.PropertyBag({});
          if (typeof current.setProperty === "function") {
            current.setProperty(part, newBag);
          } else {
            current[part] = newBag;
          }
          next = newBag;
        } else {
          next = next.getValue(now);
        }

        current = next;
      }
    }
  });
};

export const rebuildEntityPropertiesFromFlatList = (entity, flatList, keepHiddenKeys = true) => {
  if (!entity) return;
  const HIDDEN_KEYS = new Set(["feature_id", "_feature_id"]);
  const isHiddenKey = (k) => !!k && HIDDEN_KEYS.has(String(k).toLowerCase());
  const oldProps = entity.properties;

  // collect hidden keys to preserve (optional)
  const hiddenSnapshot = {};
  if (keepHiddenKeys && oldProps) {
    for (const hk of HIDDEN_KEYS) {
      const p = oldProps[hk];
      if (p) hiddenSnapshot[hk] = p;
    }
  }

  // create fresh PropertyBag
  const bag = new Cesium.PropertyBag({});
  entity.properties = bag;

  // write all visible keys from flatList
  flatList.forEach(({ key, value }) => {
    if (!key || isHiddenKey(key)) return;
    bag.addProperty?.(key);
    // simple: value → ConstantProperty
    bag[key] = new Cesium.ConstantProperty(value);
  });

  // re-attach hidden keys if needed
  Object.entries(hiddenSnapshot).forEach(([k, v]) => {
    bag.addProperty?.(k);
    bag[k] = v;
  });
};

export const cancelGeojsonEdit = (viewer, controlPointEntities, featurePointMapping, originalCameraControls) => {
  // Check if the viewer is provided
  if (!viewer) return;

  // Remove all control point entities from the viewer
  controlPointEntities.forEach((pointEntity) => {
    viewer.entities.remove(pointEntity);
  });

  // Restore the original camera controls to their previous state
  restoreCameraControls(viewer, originalCameraControls);

  // Clear the control point entities array and feature point mapping
  controlPointEntities.splice(0, controlPointEntities.length);
  featurePointMapping.clear();
};

export const createCopyPasteManager = ({
  viewer,
  dataSource,
  selectedEntitiesRef,
  pushUndo = null,
  idGenerator = () => `ent_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
  defaultOffsetMeters = { east: 10, north: 10, up: 0 },
}) => {
  // ---------- Internal clipboard ----------
  let clipboard = null; // { items: Snapshot[], anchor: Cartesian3 }
  let lastMouseCanvasPos = null; // updated by mousemove on the canvas

  // Track mouse on viewer canvas so paste can target the cursor
  const mouseMoveListener = (evt) => {
    // Store position relative to the canvas
    const rect = viewer.canvas.getBoundingClientRect();
    lastMouseCanvasPos = {
      x: evt.clientX - rect.left,
      y: evt.clientY - rect.top,
    };
  };
  viewer.canvas.addEventListener("mousemove", mouseMoveListener, { passive: true });

  // ------------- Helpers: geometry I/O -------------

  /** Return open-ring positions for any entity (no duplicate closing vertex for polygons). */
  function readPositions(ent) {
    if (ent?.polyline) {
      const arr = ent.polyline.positions?.getValue?.();
      return Array.isArray(arr) ? arr.slice() : [];
    }
    if (ent?.polygon) {
      const raw = ent.polygon.hierarchy?.getValue?.()?.positions || [];
      const arr = [...raw];
      if (arr.length > 2 && Cesium.Cartesian3.equals(arr[0], arr[arr.length - 1])) arr.pop();
      return arr;
    }
    if (ent?.position) {
      const p = ent.position?.getValue?.();
      return p ? [p] : [];
    }
    return [];
  }

  /** Write positions to an entity based on its type. */
  function writePositions(ent, type, positions) {
    if (type === "polyline") {
      ent.polyline = ent.polyline || {};
      ent.polyline.positions = positions;
    } else if (type === "polygon") {
      // Ensure closed ring for Cesium PolygonHierarchy
      const ring = positions.length ? positions.concat([positions[0]]) : positions;
      ent.polygon = ent.polygon || {};
      ent.polygon.hierarchy = new Cesium.PolygonHierarchy(ring);
    } else if (type === "point") {
      ent.position = positions[0];
    }
  }

  /** Anchor per-entity: polyline half / polygon center / point position. */
  function entityAnchor(ent) {
    const arr = readPositions(ent);
    if (ent?.polyline) return polylineHalf(arr);
    if (ent?.polygon) return polygonCenter(arr);
    if (ent?.position) return arr[0] || null;
    return null;
  }

  /** Halfway point along a polyline (length-weighted). */
  function polylineHalf(arr) {
    if (!arr || arr.length < 2) return arr?.[0] || null;
    let total = 0;
    const seg = [];
    for (let i = 0; i < arr.length - 1; i++) {
      const d = Cesium.Cartesian3.distance(arr[i], arr[i + 1]);
      seg.push(d);
      total += d;
    }
    if (total === 0) return arr[0];
    const target = total / 2;
    let acc = 0;
    for (let i = 0; i < seg.length; i++) {
      const d = seg[i];
      if (acc + d >= target) {
        const t = (target - acc) / (d || 1);
        const aC = Cesium.Cartographic.fromCartesian(arr[i]);
        const bC = Cesium.Cartographic.fromCartesian(arr[i + 1]);
        const lon = Cesium.Math.lerp(aC.longitude, bC.longitude, t);
        const lat = Cesium.Math.lerp(aC.latitude, bC.latitude, t);
        const h = Cesium.Math.lerp(aC.height, bC.height, t);
        return Cesium.Cartesian3.fromRadians(lon, lat, h);
      }
      acc += d;
    }
    return arr[arr.length - 1];
  }

  /** Geometric center for polygon using bounding sphere center + avg height. */
  function polygonCenter(arr) {
    if (!arr?.length) return null;
    const bs = Cesium.BoundingSphere.fromPoints(arr);
    const center = bs.center;
    const cCarto = Cesium.Cartographic.fromCartesian(center);
    let sumH = 0;
    for (const p of arr) sumH += Cesium.Cartographic.fromCartesian(p).height;
    const avgH = sumH / arr.length;
    return Cesium.Cartesian3.fromRadians(cCarto.longitude, cCarto.latitude, avgH);
  }

  /** Average of anchors for a group. */
  function averageAnchors(anchors) {
    if (!anchors?.length) return null;
    const acc = new Cesium.Cartesian3(0, 0, 0);
    for (const a of anchors) {
      acc.x += a.x;
      acc.y += a.y;
      acc.z += a.z;
    }
    return new Cesium.Cartesian3(acc.x / anchors.length, acc.y / anchors.length, acc.z / anchors.length);
  }

  // ------------- Helpers: pick & offsets -------------

  /** Try to get world position under a canvas pixel (with fallbacks). */
  function pickWorldFromCanvas(canvasPos) {
    if (!canvasPos) return null;
    // 1) depth picking (best)
    const p = viewer.scene.pickPosition(canvasPos);
    if (Cesium.defined(p)) return p;
    // 2) globe pick
    const ray = viewer.camera.getPickRay(canvasPos);
    if (ray && viewer.scene.globe) {
      const g = viewer.scene.globe.pick(ray, viewer.scene);
      if (Cesium.defined(g)) return g;
    }
    // 3) ellipsoid pick
    const ellipsoid = viewer.scene.globe?.ellipsoid || Cesium.Ellipsoid.WGS84;
    const e = viewer.camera.pickEllipsoid(canvasPos, ellipsoid);
    return Cesium.defined(e) ? e : null;
  }

  /** Build a small ENU offset vector (meters) at origin. */
  function enuOffset(originCart, eastM = 10, northM = 10, upM = 0) {
    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(originCart);
    const xAxis = Cesium.Matrix4.getColumn(enu, 0, new Cesium.Cartesian3());
    const yAxis = Cesium.Matrix4.getColumn(enu, 1, new Cesium.Cartesian3());
    const zAxis = Cesium.Matrix4.getColumn(enu, 2, new Cesium.Cartesian3());
    const east = Cesium.Cartesian3.multiplyByScalar(xAxis, eastM, new Cesium.Cartesian3());
    const north = Cesium.Cartesian3.multiplyByScalar(yAxis, northM, new Cesium.Cartesian3());
    const up = Cesium.Cartesian3.multiplyByScalar(zAxis, upM, new Cesium.Cartesian3());
    const tmp = Cesium.Cartesian3.add(east, north, new Cesium.Cartesian3());
    return Cesium.Cartesian3.add(tmp, up, new Cesium.Cartesian3());
  }

  // ------------- Helpers: snapshot & restore -------------

  /** Filter out helper flags from a plain properties object. */
  function stripHelperProps(obj) {
    if (!obj || typeof obj !== "object") return {};
    const out = {};
    for (const k of Object.keys(obj)) {
      if (/^is[A-Z]/.test(k)) continue; // drop flags like isControlPoint, etc.
      out[k] = obj[k];
    }
    return out;
  }

  /** Return a serializable snapshot for the given entity. */
  function snapshotEntity(ent) {
    const p = readPositions(ent);
    let type = null;
    if (ent?.polyline) type = "polyline";
    else if (ent?.polygon) type = "polygon";
    else if (ent?.position) type = "point";
    else return null;

    // Style snapshot (only common fields; extend as needed)
    const style = {};
    if (type === "polyline") {
      style.width = ent.polyline.width ?? 2.0;
      style.material = ent.polyline.material ?? Cesium.Color.YELLOW;
      style.clampToGround = ent.polyline.clampToGround ?? false;
    } else if (type === "polygon") {
      style.material = ent.polygon.material ?? Cesium.Color.YELLOW.withAlpha(0.5);
      style.outline = ent.polygon.outline ?? false;
      style.outlineColor = ent.polygon.outlineColor ?? Cesium.Color.WHITE;
      style.height = ent.polygon.height ?? undefined;
      style.heightReference = ent.polygon.heightReference ?? undefined;
      style.extrudedHeight = ent.polygon.extrudedHeight ?? undefined;
    } else if (type === "point") {
      style.pixelSize = ent.point?.pixelSize ?? 10;
      style.color = ent.point?.color ?? Cesium.Color.RED;
      style.outlineColor = ent.point?.outlineColor ?? undefined;
      style.outlineWidth = ent.point?.outlineWidth ?? undefined;
      style.heightReference = ent.point?.heightReference ?? undefined;
    }

    // Domain properties (plain object via getValue)
    let propertiesPlain = {};
    try {
      const now = Cesium.JulianDate.now();
      const raw = ent.properties?.getValue?.(now);
      propertiesPlain = stripHelperProps(raw);
    } catch (_) {
      propertiesPlain = {};
    }

    return {
      originalId: ent.id,
      name: ent.name ?? null,
      type,
      positions: p, // open ring for polygon; single for point
      style,
      propertiesPlain,
    };
  }

  /** Create a new Cesium.Entity from snapshot + world offset; returns the entity. */
  function createEntityFromSnapshot(snap, worldOffset) {
    // Shift all positions by the same world offset
    const shifted = snap.positions.map((pos) => Cesium.Cartesian3.add(pos, worldOffset, new Cesium.Cartesian3()));

    // Build init object for entities.add
    const id = idGenerator();
    const init = { id, name: snap.name ?? undefined, properties: new Cesium.PropertyBag(snap.propertiesPlain || {}) };

    if (snap.type === "polyline") {
      init.polyline = {
        positions: shifted,
        width: snap.style.width,
        material: snap.style.material,
        clampToGround: !!snap.style.clampToGround,
      };
    } else if (snap.type === "polygon") {
      const ring = shifted.concat([shifted[0]]);
      init.polygon = {
        hierarchy: new Cesium.PolygonHierarchy(ring),
        material: snap.style.material,
        outline: !!snap.style.outline,
        outlineColor: snap.style.outlineColor,
      };
      if (snap.style.height !== undefined) init.polygon.height = snap.style.height;
      if (snap.style.heightReference !== undefined) init.polygon.heightReference = snap.style.heightReference;
      if (snap.style.extrudedHeight !== undefined) init.polygon.extrudedHeight = snap.style.extrudedHeight;
    } else if (snap.type === "point") {
      init.position = shifted[0];
      init.point = {
        pixelSize: snap.style.pixelSize,
        color: snap.style.color,
        outlineColor: snap.style.outlineColor,
        outlineWidth: snap.style.outlineWidth,
        heightReference: snap.style.heightReference,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      };
    }

    const newEnt = dataSource.entities.add(init);

    // Undo payload (optional)
    if (typeof pushUndo === "function") {
      try {
        const snapshot = cloneEntityForUndo(newEnt); // lightweight clone for undo
        if (!arcDrawingActive) {
          pushUndo({ type: "add", entitySnapshot: snapshot, entityId: newEnt.id });
        }
      } catch (_) {}
    }

    return newEnt;
  }

  /** Lightweight snapshot for undo (clone geometry + style). */
  function cloneEntityForUndo(ent) {
    // This is a plain JSON-ish snapshot describing the entity
    const s = snapshotEntity(ent);
    return { ...s, id: ent.id };
  }

  // ------------- Clipboard actions -------------

  /**
   * Copy currently selected entities into the clipboard.
   * Returns number of copied entities.
   */
  function copySelected() {
    const selected = Array.isArray(selectedEntitiesRef?.current) ? selectedEntitiesRef.current : [];
    const items = [];
    const anchors = [];

    for (const ent of selected) {
      const snap = snapshotEntity(ent);
      if (!snap) continue;
      items.push(snap);
      const a = entityAnchor(ent);
      if (a) anchors.push(a);
    }

    if (!items.length) {
      clipboard = null;
      return 0;
    }

    const groupAnchor = anchors.length === 1 ? anchors[0] : averageAnchors(anchors);
    clipboard = { items, anchor: groupAnchor };

    return items.length;
  }

  /**
   * Paste clipboard at current mouse cursor (or with default ENU offset).
   * - Updates selectedEntitiesRef.current to the pasted entities.
   * - Returns the pasted entities array.
   */
  function pasteAtCursor() {
    if (!clipboard || !clipboard.items?.length) return [];

    // Determine target anchor under cursor
    let target = null;
    if (lastMouseCanvasPos) {
      target = pickWorldFromCanvas(lastMouseCanvasPos);
    }

    // Compute world offset
    let offset;
    if (target) {
      offset = Cesium.Cartesian3.subtract(target, clipboard.anchor, new Cesium.Cartesian3());
    } else {
      // Fallback: apply small ENU offset from the clipboard anchor
      const origin = clipboard.anchor || viewer.scene.camera.positionWC;
      offset = enuOffset(origin, defaultOffsetMeters.east, defaultOffsetMeters.north, defaultOffsetMeters.up);
    }

    // Create entities
    const newEntities = clipboard.items.map((snap) => createEntityFromSnapshot(snap, offset));

    // Make the pasted items the new selection
    if (selectedEntitiesRef) {
      selectedEntitiesRef.current = newEntities;
    }

    // Ensure one render after paste
    viewer.scene.requestRender?.();

    return newEntities;
  }

  /** Optional helper: clear clipboard. */
  function clearClipboard() {
    clipboard = null;
  }

  /** Check if clipboard has items. */
  function hasClipboard() {
    return !!(clipboard && clipboard.items && clipboard.items.length);
  }

  // ------------- Keyboard convenience -------------

  /**
   * Handle Ctrl/Cmd+C and Ctrl/Cmd+V.
   * Pass your own "isEditMode" boolean to guard shortcuts.
   */
  function onKeyDown(e, isEditMode = true) {
    if (!isEditMode) return;
    const key = (e.key || "").toLowerCase();
    const mod = e.ctrlKey || e.metaKey;

    if (mod && key === "c") {
      for (const key in selectedEntitiesRef.current) {
        resetEntityStyle(selectedEntitiesRef.current[key]);
      }
      e.preventDefault();
      copySelected();
    }
    if (mod && key === "v") {
      e.preventDefault();
      pasteAtCursor();
    }
  }

  // ------------- Cleanup -------------

  function destroy() {
    viewer.canvas.removeEventListener("mousemove", mouseMoveListener);
  }

  // Public API
  return {
    copySelected,
    pasteAtCursor,
    clearClipboard,
    hasClipboard,
    onKeyDown,
    destroy,
  };
};

export const deselectSelectedEntities = (selectedEntitiesRef) => {
  if (!selectedEntitiesRef || !Array.isArray(selectedEntitiesRef.current)) return;

  // --- Cesium alias (assumes Cesium is globally available) ---
  const { Color } = Cesium;

  // Small helpers for comparing colors safely
  const approx = (a, b, eps = 1e-2) => Math.abs(a - b) <= eps;

  /** Returns true if `c` looks like solid red. */
  function isRed(c) {
    return c instanceof Color && approx(c.red, 1) && approx(c.green, 0) && approx(c.blue, 0);
  }

  /** Try to undo the exact "selected" look your code applied. */
  function restoreVisuals(entity) {
    // --- Polylines ---
    if (entity.polyline) {
      const mat = entity.polyline.material;
      const width = entity.polyline.width;

      // If it looks like the "selected" style (RED + width=5), revert heuristically
      if (isRed(mat)) {
        // Set to a neutral non-selected color; choose what fits your theme best
        entity.polyline.material = Color.WHITE; // fallback neutral
      }
      if (typeof width === "number" && width >= 5) {
        entity.polyline.width = 2; // fallback width
      }
    }

    // --- Polygons ---
    if (entity.polygon) {
      const mat = entity.polygon.material;

      // If material was set to RED with alpha around 0.4, revert to a neutral fill
      if (isRed(mat)) {
        const alpha = typeof mat.alpha === "number" ? mat.alpha : 0.4;
        entity.polygon.material = Color.WHITE.withAlpha(alpha); // fallback neutral
      }

      // Always restore outline to a neutral look (your original deselect used BLACK)
      entity.polygon.outline = true;
      entity.polygon.outlineColor = Color.BLACK;
    }

    // Points: your select/deselect code didn’t change point visuals, so nothing here.
  }

  // Restore visuals for each selected entity
  for (const entity of selectedEntitiesRef.current) {
    restoreVisuals(entity);
  }

  // Clear the selection array in-place
  selectedEntitiesRef.current.length = 0;
};

export function deleteSelectedEntities(selectedEntitiesRef, pushUndo) {
  if (!selectedEntitiesRef?.current?.length) return;

  const entitiesToDelete = [...selectedEntitiesRef.current];

  entitiesToDelete.forEach((entity) => {
    const snapshot = cloneEntity(entity);
    if (!arcDrawingActive) {
      pushUndo({
        type: "delete",
        entitySnapshot: snapshot,
        entityId: entity.id,
      });
    }

    const parentCollection = entity.entityCollection || entity._collection;
    if (parentCollection) {
      parentCollection.remove(entity);
    }
  });

  selectedEntitiesRef.current.length = 0;
}

export async function offsetSelectedPolylineOnce({ viewer, distanceMeters, direction, pushUndo, defaultDataSource }) {
  // -- Basic guards
  if (!viewer || !viewer.scene || !viewer.scene.canvas) {
    console.warn("[offsetSelectedPolylineOnce] Invalid viewer.");
    return null;
  }
  if (!(typeof distanceMeters === "number" && isFinite(distanceMeters) && distanceMeters > 0)) {
    console.warn("[offsetSelectedPolylineOnce] 'distanceMeters' must be a positive number.");
    return null;
  }
  const side = String(direction || "").toLowerCase();
  if (side !== "left" && side !== "right") {
    console.warn("[offsetSelectedPolylineOnce] 'direction' must be 'left' or 'right'.");
    return null;
  }
  if (typeof pushUndo !== "function") {
    console.warn("[offsetSelectedPolylineOnce] 'pushUndo' and 'cloneEntityFn' are required for undo/redo integration.");
    return null;
  }

  // -- One-shot click handler
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
  const cleanup = () => {
    try {
      handler.destroy();
    } catch (_) {}
  };

  // -- Small helpers kept INSIDE this function to be self-contained

  /** Extract Cartesian3[] positions from an Entity polyline (supports Property). */
  const getPositions = (entity) => {
    if (!entity?.polyline?.positions) return null;
    const posProp = entity.polyline.positions;
    if (Array.isArray(posProp)) return posProp.slice();
    if (typeof posProp.getValue === "function") {
      const t = viewer?.clock?.currentTime ?? Cesium.JulianDate.now();
      const val = posProp.getValue(t);
      return Array.isArray(val) ? val.slice() : val || null;
    }
    return null;
  };

  /** Copy common visual properties from a polyline graphics. */
  const cloneStyle = (polyline) => {
    const now = Cesium.JulianDate.now();
    const width = typeof polyline.width?.getValue === "function" ? polyline.width.getValue(now) : (polyline.width ?? 2.0);
    const material = polyline.material ?? Cesium.Color.YELLOW;
    const clampToGround = typeof polyline.clampToGround?.getValue === "function" ? !!polyline.clampToGround.getValue(now) : !!polyline.clampToGround;
    const arcType = polyline.arcType ?? Cesium.ArcType.GEODESIC;
    const zIndex = typeof polyline.zIndex?.getValue === "function" ? polyline.zIndex.getValue(now) : (polyline.zIndex ?? 0);
    return { width, material, clampToGround, arcType, zIndex };
  };

  /**
   * Compute offset positions (meters) using local ENU plane at the first vertex.
   * Positive signedMeters = "left", Negative = "right" relative to vertex order.
   */
  const offsetPolylineENU = (worldPositions, signedMeters) => {
    const origin = worldPositions[0];
    const enuToEcef = Cesium.Transforms.eastNorthUpToFixedFrame(origin);
    const ecefToEnu = Cesium.Matrix4.inverse(enuToEcef, new Cesium.Matrix4());

    // Convert to ENU
    const enuPts = worldPositions.map((p) => Cesium.Matrix4.multiplyByPoint(ecefToEnu, p, new Cesium.Cartesian3()));

    // Offset each vertex laterally in EN plane
    const n = enuPts.length;
    const out = new Array(n);
    for (let i = 0; i < n; i++) {
      let dx = 0,
        dy = 0;
      if (i > 0) {
        dx += enuPts[i].x - enuPts[i - 1].x;
        dy += enuPts[i].y - enuPts[i - 1].y;
      }
      if (i < n - 1) {
        dx += enuPts[i + 1].x - enuPts[i].x;
        dy += enuPts[i + 1].y - enuPts[i].y;
      }

      let len = Math.hypot(dx, dy);
      if (len === 0 && i < n - 1) {
        dx = enuPts[i + 1].x - enuPts[i].x;
        dy = enuPts[i + 1].y - enuPts[i].y;
        len = Math.hypot(dx, dy);
      }
      if (len === 0 && i > 0) {
        dx = enuPts[i].x - enuPts[i - 1].x;
        dy = enuPts[i].y - enuPts[i - 1].y;
        len = Math.hypot(dx, dy);
      }

      if (len === 0) {
        out[i] = new Cesium.Cartesian3(enuPts[i].x, enuPts[i].y, enuPts[i].z);
        continue;
      }

      const ux = dx / len,
        uy = dy / len; // unit forward
      const leftX = -uy,
        leftY = ux; // left perpendicular
      const ox = enuPts[i].x + leftX * signedMeters;
      const oy = enuPts[i].y + leftY * signedMeters;
      out[i] = new Cesium.Cartesian3(ox, oy, enuPts[i].z); // keep height
    }

    // Back to world (ECEF)
    return out.map((p) => Cesium.Matrix4.multiplyByPoint(enuToEcef, p, new Cesium.Cartesian3()));
  };

  /** Find the DataSource that owns a given entity (fallback to defaultDataSource). */
  const findOwnerDataSource = (viewer, entity) => {
    const list = viewer?.dataSources?._dataSources || [];
    for (const ds of list) {
      if (ds?.entities?.contains?.(entity)) return ds;
    }
    // If not found (e.g., entity is in viewer.entities), prefer explicit fallback
    return defaultDataSource || null;
  };

  // -- Promise resolves after a valid polyline click
  const created = await new Promise((resolve) => {
    handler.setInputAction(async (click) => {
      try {
        const picked = viewer.scene.pick(click.position);
        const src = picked && picked.id && picked.id.polyline ? picked.id : null;
        if (!src) {
          console.warn("[offsetSelectedPolylineOnce] Click on a polyline entity to proceed.");
          return; // keep listening
        }

        const positions = getPositions(src);
        if (!positions || positions.length < 2) {
          console.warn("[offsetSelectedPolylineOnce] Polyline has insufficient positions.");
          cleanup();
          resolve(null);
          return;
        }

        const signed = side === "left" ? distanceMeters : -distanceMeters;
        const offsetPositions = offsetPolylineENU(positions, signed);

        const style = cloneStyle(src.polyline);

        // Decide where to store the new entity (same DataSource as source, otherwise fallback)
        const ownerDS = findOwnerDataSource(viewer, src);
        const ownerEntities = ownerDS?.entities || viewer.entities; // last resort viewer.entities

        // Create the parallel entity
        const newEntity = ownerEntities.add({
          polyline: {
            positions: offsetPositions,
            width: style.width,
            material: style.material,
            clampToGround: style.clampToGround,
            arcType: style.arcType,
            zIndex: style.zIndex,
          },
          // Optionally carry over a readable name
          name: src.name ? `${src.name} (offset ${distanceMeters}m ${side})` : undefined,
          properties: src.properties, // keep properties if needed
        });

        // Push UNDO action so your undo/redo/cancel flows handle this "add"
        try {
          const snapshot = cloneEntity(newEntity); // must be a safe snapshot for re-adding later
          if (!arcDrawingActive) {
            pushUndo({
              type: "add",
              entitySnapshot: snapshot,
              entityId: newEntity.id,
              dsRef: ownerDS || undefined, // helps your batch/cancel resolver pick correct collection
            });
          }
        } catch (e) {
          console.warn("[offsetSelectedPolylineOnce] Failed to push undo action:", e);
        }

        cleanup();
        resolve(newEntity);
      } catch (err) {
        console.error("[offsetSelectedPolylineOnce] Error:", err);
        cleanup();
        resolve(null);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  });

  return created;
}
